import { useContext } from 'react';
import { useRecoilValue } from 'recoil';
import { SocketContext } from '@/contexts/socket';
import { currentThemeOverridesSelector, isCustomAccentSelector, isCustomBackgroundSelector, themeColorsSelector, welcomeScreenSelector, } from '@/state/config';
import { chatEventsState, sessionIdSelector, showWelcomeFormSelector, } from '@/state/session';
import { isLoadingSelector } from '@/state/socket';
import { SocketEvent } from '@/ts/socket';
import { getTimestamp } from '@/util/utils';
/**
 * Determines the header icons color classes based on widget state
 * Supports custom icon color via --moveo-header-text-color CSS variable
 * @param isWelcomeScreenEnabled - Whether the welcome screen is currently displayed
 * @param isCustomBackground - Whether a custom background color is configured
 * @param isCustomAccent - Whether a custom accent color is configured
 * @returns Tailwind text color classes for icons
 */
export const getIconsColor = (isWelcomeScreenEnabled, isCustomBackground, isCustomAccent) => {
    // When custom accent is set: use custom icon color with primary foreground fallback
    if (isCustomAccent) {
        return 'text-[var(--moveo-header-text-color,var(--color-text-primary-foreground))]';
    }
    // When custom background is set: use custom icon color with white fallback
    if (isCustomBackground) {
        return 'text-[var(--moveo-header-text-color,white)]';
    }
    // When welcome screen is enabled: use custom icon color with element foreground fallback
    if (isWelcomeScreenEnabled) {
        return 'text-[var(--moveo-header-text-color,var(--color-element-primary-foreground))] hover:text-[var(--color-accent)]';
    }
    // Default case: use custom icon color with accent fallback
    return 'text-[var(--moveo-header-text-color,var(--color-accent))]';
};
/**
 * Determines the header background color classes based on widget state
 * @param isWelcomeScreenEnabled - Whether the welcome screen is currently displayed
 * @param isCustomBackground - Whether a custom background color is configured
 * @returns Tailwind background classes for the header
 */
export const getHeaderBackground = (isWelcomeScreenEnabled, isCustomBackground) => {
    // When welcome screen is enabled: use custom header background with accent fallback
    // Also apply dark mode text foreground and remove border
    if (isWelcomeScreenEnabled) {
        return 'dark:bg-text-foreground border-none bg-[var(--moveo-header-background,var(--color-accent))]';
    }
    // When custom background is set: use custom header background with accent fallback
    if (isCustomBackground) {
        return 'bg-[var(--moveo-header-background,var(--color-accent))]';
    }
    // Default case: use only the custom header background variable without fallback
    return 'bg-[var(--moveo-header-background)]';
};
/**
 * Determines the header text color classes based on widget state
 * Supports custom text color via --moveo-header-text-color CSS variable
 * @param isCustomBackground - Whether a custom background color is configured
 * @param isCustomAccent - Whether a custom accent color is configured
 * @returns Tailwind text color classes
 */
export const getHeaderTextColor = (isCustomBackground, isCustomAccent) => {
    // Always prioritize the custom header text color variable if set
    // Falls back to white for custom backgrounds, primary foreground for custom accent
    if (isCustomAccent) {
        return 'text-[var(--moveo-header-text-color,var(--color-text-primary-foreground))]';
    }
    // When custom background is set: default to white but allow override
    if (isCustomBackground) {
        return 'text-[var(--moveo-header-text-color,white)]';
    }
    // Default case: use custom text color with foreground fallback
    return 'text-[var(--moveo-header-text-color,var(--color-foreground))]';
};
export const formatTranscriptFromHistory = (events) => {
    if (!(events === null || events === void 0 ? void 0 : events.length)) {
        return '';
    }
    // CSV headers - new structure
    const headers = [
        'Timestamp (UTC)',
        'Author (type)',
        'Message',
        'Message type',
        'Extra',
    ];
    // Helper function to format timestamp in UTC
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toISOString();
    };
    // Helper function to escape CSV values
    const escapeCsvValue = (value) => {
        if (!value) {
            return '';
        }
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        const escaped = value.replace(/"/g, '""');
        if (escaped.includes(',') ||
            escaped.includes('"') ||
            escaped.includes('\n')) {
            return `"${escaped}"`;
        }
        return escaped;
    };
    // Helper function to extract message content
    const extractMessageContent = (event) => {
        var _a, _b;
        switch (event.event_type) {
            case 'user_message':
                return event.text || '';
            case 'agent_message':
                return ((_a = event.body) === null || _a === void 0 ? void 0 : _a.text) || event.text || '';
            case 'brain_message':
                if (event.response.type === 'text') {
                    return event.response.text || '';
                }
                else if (event.response.type === 'carousel') {
                    return `Carousel with ${((_b = event.response.cards) === null || _b === void 0 ? void 0 : _b.length) || 0} cards`;
                }
                else if (event.response.type === 'video') {
                    return `Video: ${event.response.url} `;
                }
                else if (event.response.type === 'image') {
                    return `Image: ${event.response.url}`;
                }
                else if (event.response.type === 'url') {
                    return `Url: ${event.response.url}`;
                }
                else if (event.response.type === 'webview') {
                    return `Webview: ${event.response.url}`;
                }
                return `${event.response.type} response`;
            case 'member_join':
                return `${event.author_name} joined the conversation`;
            case 'member_leave':
                return `${event.author_name} left the conversation`;
            default:
                return '';
        }
    };
    // Helper function to get author with type
    const getAuthorWithType = (event) => {
        const authorName = event.author_name === 'common.ai_agent' ? 'AI Agent' : event.author_name;
        let authorType = '';
        switch (event.event_type) {
            case 'user_message':
                authorType = 'visitor';
                break;
            case 'agent_message':
                authorType = 'agent';
                break;
            case 'brain_message':
                authorType = 'ai_agent';
                break;
            case 'member_join':
            case 'member_leave':
                authorType = event.author_type;
                break;
            default:
                authorType = '';
        }
        return `${authorName} (${authorType})`;
    };
    // Helper function to get message type
    const getMessageType = (event) => {
        switch (event.event_type) {
            case 'user_message':
                return 'user_message';
            case 'agent_message':
                return 'agent_message';
            case 'brain_message':
                return event.response.type;
            case 'member_join':
                return 'member_join';
            case 'member_leave':
                return 'member_leave';
            default:
                return '';
        }
    };
    // Helper function to get timestamp
    const getTimestamp = (event) => {
        switch (event.event_type) {
            case 'user_message':
            case 'brain_message':
                return event.created_at;
            case 'agent_message':
                // AgentMessageEvent doesn't have created_at, we can use read_at or return undefined
                return event.read_at;
            case 'member_join':
            case 'member_leave':
                // These don't have timestamps in the type definition
                return undefined;
            default:
                return undefined;
        }
    };
    // Helper function to build extra information
    const getExtraInfo = (event) => {
        const extraParts = [];
        // Add request ID for brain messages
        if (event.event_type === 'brain_message' && event.request_id) {
            extraParts.push(`Request ID: ${event.request_id}`);
        }
        // Add author ID
        if (event.author_id) {
            extraParts.push(`Author ID: ${event.author_id}`);
        }
        // Add delivered_at for user messages
        if (event.event_type === 'user_message' && event.delivered_at) {
            extraParts.push(`Delivered: ${new Date(event.delivered_at).toISOString()}`);
        }
        // Add read_at if available (check if property exists)
        if ('read_at' in event && event.read_at) {
            extraParts.push(`Read: ${new Date(event.read_at).toISOString()}`);
        }
        // Add action ID for brain messages
        if (event.event_type === 'brain_message' && event.response.action_id) {
            extraParts.push(`Action ID: ${event.response.action_id}`);
        }
        // Add file/media specific info
        if (event.event_type === 'brain_message') {
            const response = event.response;
            if (response.type === 'video' || response.type === 'file') {
                if (response.name) {
                    extraParts.push(`Name: ${response.name}`);
                }
                if (response.size) {
                    extraParts.push(`Size: ${response.size} bytes`);
                }
            }
            else if (response.type === 'image') {
                if (response.name) {
                    extraParts.push(`Name: ${response.name}`);
                }
            }
            if (response.type === 'webview') {
                if (response.label) {
                    extraParts.push(`Label: ${response.label}`);
                }
                if (response.trigger_node_id) {
                    extraParts.push(`Trigger Node: ${response.trigger_node_id}`);
                }
                if (response.height) {
                    extraParts.push(`Height: ${response.height}`);
                }
            }
        }
        return extraParts.join(' | ');
    };
    // Convert events to CSV rows
    const csvRows = [headers.join(',')];
    const filteredEvents = events.filter((e) => { var _a; return ((_a = e.response) === null || _a === void 0 ? void 0 : _a.type) !== 'pause'; });
    filteredEvents.forEach((event) => {
        const timestamp = getTimestamp(event);
        const timestampStr = timestamp ? formatTimestamp(timestamp) : '';
        const authorWithType = getAuthorWithType(event);
        const message = extractMessageContent(event);
        const messageType = getMessageType(event);
        const extra = getExtraInfo(event);
        const row = [
            escapeCsvValue(timestampStr),
            escapeCsvValue(authorWithType),
            escapeCsvValue(message),
            escapeCsvValue(messageType),
            escapeCsvValue(extra),
        ];
        csvRows.push(row.join(','));
    });
    return csvRows.join('\n');
};
export const useHeader = () => {
    const events = useRecoilValue(chatEventsState);
    const sessionId = useRecoilValue(sessionIdSelector);
    const socket = useContext(SocketContext);
    const isCustomBackground = useRecoilValue(isCustomBackgroundSelector);
    const isCustomAccent = useRecoilValue(isCustomAccentSelector);
    const welcomeScreen = useRecoilValue(welcomeScreenSelector);
    const isLoading = useRecoilValue(isLoadingSelector);
    const showWelcomeForm = useRecoilValue(showWelcomeFormSelector);
    const cssOverrides = useRecoilValue(currentThemeOverridesSelector);
    const themeColors = useRecoilValue(themeColorsSelector);
    // Check if custom header background is set via setCSSVariables() OR backend theme_colors
    const hasCustomHeaderBackground = Boolean((cssOverrides === null || cssOverrides === void 0 ? void 0 : cssOverrides['--moveo-header-background']) || (themeColors === null || themeColors === void 0 ? void 0 : themeColors.header_color));
    const isWelcomeScreenEnabled = (welcomeScreen === null || welcomeScreen === void 0 ? void 0 : welcomeScreen.enabled) &&
        events.length === 0 &&
        !isLoading &&
        !showWelcomeForm;
    // When custom header background is set, use white icons/text with white hover
    // Otherwise, use accent color for icons and highlight-background for hover
    const iconsColor = hasCustomHeaderBackground
        ? 'text-[var(--moveo-header-text-color,white)]'
        : getIconsColor(isWelcomeScreenEnabled, isCustomBackground, isCustomAccent);
    const hoverIconColor = hasCustomHeaderBackground
        ? 'hover:bg-white/10'
        : 'hover:bg-highlight-background';
    const headerBackground = getHeaderBackground(!!isWelcomeScreenEnabled, !!isCustomBackground);
    // When custom header background is set, text should match icons color
    const headerTextColor = hasCustomHeaderBackground
        ? 'text-[var(--moveo-header-text-color,white)]'
        : getHeaderTextColor(!!isCustomBackground, !!isCustomAccent);
    const handleConversationDownload = () => {
        const csvContent = formatTranscriptFromHistory(events);
        if (!csvContent) {
            return;
        }
        // Add UTF-8 BOM to ensure proper encoding for Greek and other Unicode characters
        const BOM = '\uFEFF';
        const csvWithBOM = BOM + csvContent;
        // Create and download CSV file
        const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `conversation-${sessionId}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up the URL object
        URL.revokeObjectURL(url);
    };
    const handleSendConversationViaEmail = (email) => {
        const csvContent = formatTranscriptFromHistory(events);
        if (!csvContent) {
            return;
        }
        if (socket && sessionId) {
            socket.emit(SocketEvent.conversation_email_send, {
                session_id: sessionId,
                timestamp: getTimestamp(),
                email,
                body: csvContent,
            });
        }
    };
    return {
        handleConversationDownload,
        handleSendConversationViaEmail,
        iconsColor,
        hoverIconColor,
        headerBackground,
        headerTextColor,
        isWelcomeScreenEnabled,
        isCustomBackground,
    };
};
